import { ApolloClient, ApolloLink, HttpLink, InMemoryCache, type NormalizedCacheObject } from "@apollo/client"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { ErrorLink } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import { LocalState } from "@apollo/client/local-state"
import { ApolloProvider } from "@apollo/client/react"
import { type Reference, relayStylePagination } from "@apollo/client/utilities"
// @ts-expect-error - No types for this package
import UploadHttpLink from "apollo-upload-client/UploadHttpLink.mjs"
import fetch from "cross-fetch"
import { type OperationDefinitionNode } from "graphql"
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink"
import { merge } from "lodash-es"
import React, { useMemo } from "react"
import generatedIntrospection from "./types.json"
import {
  BasketStateDocument,
  type BasketStateQuery,
  CheckoutStateDocument,
  type CheckoutStateQuery,
  type SetBasketStateMutationVariables,
} from "@/api/graphql"
import { getAdminSessionKey } from "@/common/auth"
import { consumer } from "@/common/cable"

declare global {
  interface Window {
    __APOLLO_STATES__?: NormalizedCacheObject[]
  }
}

declare module "@apollo/client" {
  export interface DefaultContext extends Record<string, any> {
    retry?: boolean
    hasUpload?: boolean
  }
}

export type CachedRelayEdge = {
  cursor?: string
  node: Reference
}

export type TRelayPageInfo = {
  hasPreviousPage: boolean
  hasNextPage: boolean
  startCursor: string
  endCursor: string
}

export type CachedRelayConnection = {
  edges?: CachedRelayEdge[]
  pageInfo?: TRelayPageInfo
}

const retryNetworkError = (error: Error | null) => {
  if (!error) {
    return false
  }

  // query/mutation aborted by AbortController
  if (error.name === "AbortError") {
    return false
  }

  console.error(`[Network error]: ${error}`)

  // Automatically retry if there was something wrong with the network, not for server errors.
  return (
    error.message.includes("Failed to fetch") ||
    error.message.includes("NetworkError when attempting to fetch") ||
    error.message.includes("cancelled")
  )
}

const linkWithRetry = (link: ApolloLink) => {
  return ApolloLink.from([
    // persistedQueriesLink,
    new RetryLink({
      attempts: {
        retryIf(error, operation) {
          return operation.getContext().retry || retryNetworkError(error)
        },
      },
    }),
    link,
  ])
}

export interface CreateClientOptions {
  uri?: string
  sessionKey?: string | null
  locale?: string
  businessUser?: boolean
}

export const createClient = (opts?: CreateClientOptions) => {
  const headers: Record<string, string> = {}

  if (opts?.sessionKey) {
    headers["X-Admin-Session-Key"] = opts.sessionKey
  }

  if (opts?.locale) {
    headers["X-Skyltmax-Locale"] = opts.locale
  }

  if (opts?.businessUser) {
    headers["X-Skyltmax-Business-User"] = opts.businessUser ? "true" : "false"
  }

  if (import.meta.env.SSR) {
    headers["X-Skyltmax-SSR"] = "true"
  }

  const uri = opts?.uri || "/graphql"

  let link = linkWithRetry(
    new HttpLink({
      uri,
      credentials: "same-origin",
      headers,
      fetch,
    })
  )

  if (!import.meta.env.SSR) {
    link = ApolloLink.split(
      (operation: ApolloLink.Operation) => operation.getContext().hasUpload || false,
      linkWithRetry(
        new UploadHttpLink({
          uri,
          credentials: "same-origin",
          headers,
          fetch,
        })
      ),
      link
    )

    const hasSubscriptionOperation = ({ query: { definitions } }: ApolloLink.Operation) => {
      return (definitions as OperationDefinitionNode[]).some(
        ({ kind, operation }) => kind === "OperationDefinition" && operation === "subscription"
      )
    }

    link = ApolloLink.split(hasSubscriptionOperation, new ActionCableLink({ cable: consumer }), link)
  }

  const initialStates = typeof window !== "undefined" ? window.__APOLLO_STATES__ || [] : []

  const client = new ApolloClient({
    link: ApolloLink.from([
      new ErrorLink(({ error, forward, operation }) => {
        if (CombinedGraphQLErrors.is(error)) {
          error.errors.forEach(gqlError => {
            const { message, locations, path } = gqlError

            if (message === "login_required") {
              console.error("login_required")

              if (import.meta.env.SSR) {
                throw new Error("login_required")
              } else if (location.href.includes("admin.")) {
                location.reload()
              }

              return
            }

            console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
          })

          return
        }

        return forward(operation)
      }),
      link,
    ]),
    cache: new InMemoryCache({
      possibleTypes: generatedIntrospection.possibleTypes,
      typePolicies: {
        Hash: {
          keyFields: false,
        },
        BasketState: {
          keyFields: false,
        },
        Query: {
          fields: {
            signs: relayStylePagination(),
            bundles: relayStylePagination(),
            customers: relayStylePagination(),
            orders: relayStylePagination(),
          },
        },
      },
    }).restore(
      merge(
        {
          ROOT_QUERY: {
            basketState: {
              open: false,
              addedNotice: false,
              reload: false,
              excelImportNotice: false,
              __typename: "BasketState",
            },
          },
        },
        ...initialStates
      )
    ),
    localState: new LocalState({
      resolvers: {
        Mutation: {
          setBasketState: (_root, variables: SetBasketStateMutationVariables, context) => {
            const cache = context.client.cache as InMemoryCache
            const data = cache.readQuery<BasketStateQuery>({ query: BasketStateDocument })

            variables.addedNotice = variables.addedNotice || false
            variables.excelImportNotice = variables.excelImportNotice || false
            variables.reload = variables.reload || false
            cache.writeQuery<BasketStateQuery>({
              query: BasketStateDocument,
              data: { basketState: { ...data?.basketState, ...variables } },
            })

            return null
          },
        },
      },
    }),
    ssrMode: import.meta.env.SSR,
  })

  return client
}

const defaultClient = createClient({ sessionKey: getAdminSessionKey() })

export interface ProviderProps {
  state?: {
    checkout?: CheckoutStateQuery
  }
  orderId?: number
}

interface ProviderRenderProps extends ProviderProps {
  children?: React.ReactNode
  client?: ApolloClient
}

export const ApolloProviderPreloader = <TProps extends ProviderRenderProps>(props: TProps) => {
  const client = props.client || defaultClient

  useMemo(() => {
    if (props.state?.checkout && props.orderId) {
      client.writeQuery({
        query: CheckoutStateDocument,
        variables: { id: props.orderId.toString() },
        data: props.state.checkout,
      })
    }
  }, [props.orderId, props.state, client])

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>
}

export default defaultClient
